import React from "react";
import styled from "@emotion/styled";
import {
  Alert as MuiAlert,
  AlertTitle,
  Divider as MuiDivider,
  Grid,
  IconButton,
  InputBase,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Button,
  CircularProgress as MuiCircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { spacing } from "@mui/system";
import { tableCellClasses } from "@mui/material/TableCell";
import { fetchAllLocationSettings } from "../../../../../redux/slices/locationSettings";
import { useDispatch, useSelector } from "react-redux";
import { settingsMenu } from "../../../../../redux/slices/settings";
import { isValidToken } from "../../../../../utils/jwt";
import {
  fetchLogOut,
  fetchLoginLogOut_Socket,
  logout,
} from "../../../../../redux/slices/login";
import { useNavigate } from "react-router-dom";

const CircularProgress = styled(MuiCircularProgress)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Alert = styled(MuiAlert)(spacing);
const Divider = styled(MuiDivider)(spacing);

const Spacer = styled.div`
  flex: 1 1 20%;
`;

const Search = styled.div`
  margin-top: 5px;
  margin-left: 5px;
  margin-right: 5px;
  background-color: #e7f2ff;
`;

const CustomTableCell = styled(TableCell)`
  &.${tableCellClasses.head} {
    background: ${(props) => props.theme.palette.tertiary.main};
    color: ${(props) => props.theme.palette.common.black};
    margin-right: 10px;
  }
  &.${tableCellClasses.body} {
    font-size: 14px;
  }
`;

function createData(location_name, description, icon, _id) {
  return { location_name, description, icon, _id };
}

let rows = [];
let rowsData = [];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => ({
    el,
    index,
  }));
  stabilizedThis.sort((a, b) => {
    const order = comparator(a.el, b.el);
    if (order !== 0) return order;
    return a.index - b.index;
  });
  return stabilizedThis.map((element) => element.el);
}

const headCells = [
  {
    id: "location_name",
    numeric: false,
    disablePadding: true,
    label: "Location Name",
  },
  {
    id: "description",
    numeric: false,
    disablePadding: true,
    label: "Description",
  },
  {
    id: "icon",
    numeric: false,
    disablePadding: true,
    label: "Icon",
  },
  {
    id: "action",
    numeric: false,
    disablePadding: true,
    label: "Actions",
  },
];

const EnhancedTableHead = (props) => {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <CustomTableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding="checkbox"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </CustomTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

function EnhancedTable({ locationSettings, status, dispatch, accountData }) {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("calories");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(true);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = React.useState("");
  const accessToken = window.localStorage.getItem("accessToken");
  const navigate = useNavigate();

  React.useEffect(() => {
    setDense(true);
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  const handleAdd = () => {
    if (!isValidToken(accessToken)) {
      dispatch(fetchLogOut());
      dispatch(fetchLoginLogOut_Socket());
      dispatch(logout());
      navigate("/");
    } else {
      dispatch(
        settingsMenu({
          list: "locationsettings_add",
          listData: [],
        })
      );
    }
  };

  const handleEdit = (row) => {
    if (!isValidToken(accessToken)) {
      dispatch(fetchLogOut());
      dispatch(fetchLoginLogOut_Socket());
      dispatch(logout());
      navigate("/");
    } else {
      dispatch(
        settingsMenu({
          list: "locationsettings_edit",
          listData: row,
        })
      );
    }
  };

  const handleDelete = (row) => {
    if (!isValidToken(accessToken)) {
      dispatch(fetchLogOut());
      dispatch(fetchLoginLogOut_Socket());
      dispatch(logout());
      navigate("/");
    } else {
      dispatch(
        settingsMenu({
          list: "locationsettings_delete",
          listData: {
            _id: row._id,
            location_name: row.location_name,
            description: row.description,
            icon: row.icon,
          },
        })
      );
    }
  };

  const handleAgentMapping = () => {
    if (!isValidToken(accessToken)) {
      dispatch(fetchLogOut());
      dispatch(fetchLoginLogOut_Socket());
      dispatch(logout());
      navigate("/");
    } else {
      dispatch(
        settingsMenu({
          list: "locationsettings_agent_mapping",
          listData: [],
        })
      );
    }
  };

  let emptyRows = rowsPerPage - (rowsPerPage + 1);
  rowsData =
    locationSettings &&
    locationSettings.data &&
    Array.isArray(locationSettings.data)
      ? locationSettings.data.map((item) =>
          createData(item.location_name, item.description, item.icon, item._id)
        )
      : [];

  rows =
    search === ""
      ? rowsData
      : rowsData.filter(
          (i) =>
            i.location_name.toLowerCase().includes(search.toLowerCase()) ||
            i.description.toLowerCase().includes(search.toLowerCase())
        );

  const searchFilter = (e) => {
    setPage(0);
    setSearch(e.target.value);
  };

  return (
    <div>
      <Paper>
        <Toolbar>
          <div>
            <Typography variant="h6" id="tableTitle">
              Location Settings List
            </Typography>
          </div>
          <Spacer />
          <div>
            {/* {accountData && accountData.sticky_location !== 0 ? ( */}
            {accountData?.sticky_location === 1 ? (
              <>
                <Tooltip title="Add new location setting">
                  <Button
                    mr={2}
                    variant="contained"
                    color="primary"
                    style={{ marginRight: "5px" }}
                    onClick={handleAdd}
                  >
                    Add
                    <AddIcon />
                  </Button>
                </Tooltip>
                <Tooltip title="Map agents to locations">
                  <Button
                    mr={2}
                    variant="contained"
                    color="primary"
                    style={{ marginRight: "5px" }}
                    onClick={handleAgentMapping}
                  >
                    Agent Mapping
                    <PeopleIcon />
                  </Button>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="Location Settings are disabled in Account Settings">
                <div>
                  <Button
                    mr={2}
                    variant="contained"
                    color="primary"
                    style={{ marginRight: "5px", opacity: 0.5 }}
                    disabled
                  >
                    Add
                    <AddIcon />
                  </Button>
                </div>
              </Tooltip>
            )}
          </div>
        </Toolbar>
        <Search>
          <IconButton type="button" sx={{ p: "4px" }} aria-label="search">
            <SearchIcon />
          </IconButton>
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Search"
            onChange={searchFilter}
          />
        </Search>
        <Divider my={2} sx={{ borderBottomWidth: 2 }} />
        <TableContainer>
          {!!status && status === "loading" && (
            <CircularProgress m={2} color="primary" />
          )}
          {!!locationSettings &&
            !!locationSettings.status &&
            parseInt(locationSettings.status) !== 200 && (
              <Alert mb={4} severity="error">
                <AlertTitle>
                  Error: (status - code: {locationSettings.status})
                </AlertTitle>
                {locationSettings.message}— <strong>check it out!</strong>
              </Alert>
            )}
          {!!status && status === "error" && (
            <Alert
              severity="error"
              sx={{
                p: "2px !important",
                mt: "3px !important",
                mb: "3px !important",
              }}
            >
              <AlertTitle>Error:</AlertTitle>
              <strong>Something went wrong</strong>
            </Alert>
          )}
          <Table
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {!!rows &&
                stableSort(rows, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const isItemSelected = isSelected(row.name);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        onClick={(event) =>
                          handleClick(event, row.location_name)
                        }
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.srno}
                        selected={isItemSelected}
                      >
                        <TableCell
                          component="th"
                          id={labelId}
                          scope="row"
                          padding="checkbox"
                        >
                          {row.location_name}
                        </TableCell>
                        <TableCell align="left">{row.description}</TableCell>
                        <TableCell align="left">{row.icon}</TableCell>
                        <TableCell align="left">
                          <EditIcon
                            variant="contained"
                            color="primary"
                            style={{
                              marginRight: "5px",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              handleEdit(row);
                            }}
                          />
                          <DeleteIcon
                            variant="contained"
                            color="primary"
                            style={{
                              marginRight: "5px",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              handleDelete(row);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}

function LocationSettingsTable({ accountData }) {
  const dispatch = useDispatch();
  const { data: locationSettings, status } = useSelector(
    (state) => state.locationSettings
  );

  React.useEffect(() => {
    dispatch(fetchAllLocationSettings());
  }, [dispatch]);

  return (
    <React.Fragment>
      <Grid container spacing={3} style={{ padding: "10px" }}>
        <Grid item xs={12}>
          <EnhancedTable
            locationSettings={locationSettings}
            status={status}
            dispatch={dispatch}
            accountData={accountData}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default LocationSettingsTable;
